import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Package, Calendar, CreditCard, CheckCircle, ArrowLeft } from 'lucide-react';

export const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrder();
  }, [user, orderId, navigate]);

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-purple-25">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-purple-25 px-6">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-light mb-4">Order Not Found</h2>
          <Button onClick={() => navigate('/orders')} className="rounded-full">
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-purple-25 py-12 px-6 lg:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/orders')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>

        {/* Order Header */}
        <div className="bg-white rounded-2xl p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-heading font-light mb-2">Order Details</h1>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-purple-600" />
                <span className="text-lg font-medium text-purple-900">
                  {order.tracking_number || order.id}
                </span>
              </div>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${
              order.status === 'completed'
                ? 'bg-green-100 text-green-800'
                : order.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-stone-100 text-stone-800'
            }`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-6 py-6 border-t border-stone-200">
            <div>
              <div className="flex items-center gap-2 text-stone-500 mb-2">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">Order Date</span>
              </div>
              <p className="font-medium">{formatDate(order.created_at)}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-stone-500 mb-2">
                <CreditCard className="h-4 w-4" />
                <span className="text-sm">Payment Method</span>
              </div>
              <p className="font-medium capitalize">{order.payment_method}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-stone-500 mb-2">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Total Amount</span>
              </div>
              <p className="text-2xl font-semibold text-purple-900">
                ${order.total_amount.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-2xl p-8 mb-6">
          <h2 className="text-2xl font-heading font-light mb-6">Order Items</h2>
          <div className="space-y-4">
            {order.items && order.items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl"
              >
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.product_name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-medium text-lg mb-1">{item.product_name}</h3>
                  <p className="text-sm text-stone-600">Size: {item.size}</p>
                  <p className="text-sm text-stone-600">Quantity: {item.quantity}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-purple-900">
                    ${item.price.toFixed(2)}
                  </p>
                  <p className="text-sm text-stone-500">
                    ${(item.price * item.quantity).toFixed(2)} total
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Status Timeline */}
        <div className="bg-white rounded-2xl p-8">
          <h2 className="text-2xl font-heading font-light mb-6">Order Status</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium">Order Placed</p>
                <p className="text-sm text-stone-500">{formatDate(order.created_at)}</p>
              </div>
            </div>

            {order.status === 'completed' && (
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Payment Confirmed</p>
                  <p className="text-sm text-stone-500">Payment successfully processed</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center">
                <Package className="h-5 w-5 text-stone-500" />
              </div>
              <div>
                <p className="font-medium text-stone-500">Preparing for Shipment</p>
                <p className="text-sm text-stone-400">We'll notify you when it ships</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
